<div
  id="user-settings-usage"
  [class.settings-panel]="!embedded"
  [class.embedded]="embedded"
  [class.mobile]="isMobile"
>
  <div class="head" *ngIf="!embedded">
    <span class="material-icons go-back" (click)="goBack.emit()"> arrow_back </span>
    <div class="description">
      <div class="title">{{ PANELS.USAGE.name }}</div>
      <div class="subtitle">{{ PANELS.USAGE.description }}</div>
    </div>
    <span class="material-icons close" (click)="goBack.emit()"> close </span>
  </div>
  <div class="sub-head" *ngIf="isMobile && !embedded">
    {{ PANELS.USAGE.description }}
  </div>

  <div class="panel-body">
    <div class="usage-scroll">
      <section class="limits" *ngIf="limits">
        <div class="paused" *ngIf="limits.capped as c" role="status">
          <span class="material-icons paused-icon">pause_circle</span>
          <div>
            <strong>{{ 'AI requests are paused.' | translate }}</strong>
            {{ 'This workspace has used its' | translate }} {{ c.kind }} {{ 'AI credits' | translate }}
            ({{ c.used | number: '1.0-2' }} {{ 'of' | translate }} {{ c.cap | number: '1.0-2' }}).
            <ng-container *ngIf="c.resetsAt; else contact">
              {{ 'They reset on' | translate }} {{ c.resetsAt | date: 'MMM d, h:mm a' }}.
            </ng-container>
            <ng-template #contact>{{ 'Contact support to raise the limit.' | translate }}</ng-template>
          </div>
        </div>
        <div class="limits-head">
          <h3 class="block-title">{{ 'Credit limits' | translate }}</h3>
          <span class="muted" *ngIf="limits.unlimited">{{ 'No credit limits on this workspace.' | translate }}</span>
        </div>
        <ul class="limit-rows" *ngIf="limitRows.length">
          <li class="limit" *ngFor="let l of limitRows" [class.limit--full]="l.fraction >= 1" [class.limit--near]="l.fraction >= 0.8 && l.fraction < 1">
            <div class="limit-main">
              <span class="limit-name">{{ l.label | translate }}</span>
              <span class="limit-value">{{ l.used | number: '1.0-2' }} / {{ l.cap | number: '1.0-2' }}</span>
            </div>
            <div class="share"><span class="share-fill limit-fill" [style.width.%]="l.fraction * 100"></span></div>
            <div class="row-sub">
              <ng-container *ngIf="l.kind === 'total'">{{ 'All usage to date' | translate }}</ng-container>
              <ng-container *ngIf="l.kind !== 'total' && l.notStarted">{{ 'Starts with the next AI request' | translate }}</ng-container>
              <ng-container *ngIf="l.kind !== 'total' && !l.notStarted">{{ 'Resets' | translate }} {{ l.resetsAt | date: 'MMM d, h:mm a' }}</ng-container>
            </div>
          </li>
        </ul>
      </section>

      <div class="toolbar">
        <label class="period">
          <span class="period-label">{{ 'Month' | translate }}</span>
          <select
            class="period-select"
            [value]="selected"
            (change)="selectMonth(+$any($event.target).value)"
          >
            <option *ngFor="let m of months; let i = index" [value]="i">
              {{ i === 0 ? ('This month' | translate) : (m.from | date: 'MMMM yyyy' : 'UTC') }}
            </option>
          </select>
        </label>
      </div>

      <div class="state" *ngIf="isLoading && !report">{{ 'Loading…' | translate }}</div>
      <div class="state" *ngIf="!isLoading && failed">
        {{ 'Usage could not be loaded. Try again in a moment.' | translate }}
      </div>

      <div class="empty" *ngIf="!failed && report && !hasUsage">
        <span class="material-icons empty-icon">data_usage</span>
        <p>{{ 'No AI usage in this month.' | translate }}</p>
      </div>

      <ng-container *ngIf="report && hasUsage">
        <div class="stats" [class.is-stale]="isLoading">
          <div class="stat stat--total">
            <span class="stat-label">{{ 'Total' | translate }}</span>
            <span class="stat-value">{{ report.totalCredits | number: '1.0-2' }}</span>
            <span class="stat-unit">{{ 'credits' | translate }}</span>
          </div>
          <div class="stat">
            <span class="stat-label"><i class="swatch swatch--ai"></i>{{ 'AI agent' | translate }}</span>
            <span class="stat-value">{{ ai?.credits || 0 | number: '1.0-2' }}</span>
            <span class="stat-unit">{{ 'credits' | translate }}</span>
          </div>
          <div class="stat">
            <span class="stat-label"><i class="swatch swatch--voice"></i>{{ 'Voice' | translate }}</span>
            <span class="stat-value">{{ voice?.credits || 0 | number: '1.0-2' }}</span>
            <span class="stat-unit">
              {{ 'credits' | translate }}
              <ng-container *ngIf="voice?.minutes">
                · {{ voice?.minutes | number: '1.0-0' }} {{ 'min' | translate }}
              </ng-container>
            </span>
          </div>
        </div>

        <section class="block" [class.is-stale]="isLoading">
          <h3 class="block-title">{{ 'Credits per day' | translate }}</h3>
          <div class="chart" role="img" [attr.aria-label]="'Credits per day' | translate">
            <span class="chart-max">{{ maxDay | number: '1.0-2' }}</span>
            <div class="bars">
              <div
                class="bar"
                *ngFor="let d of days"
                [title]="(d.date | date: 'MMM d' : 'UTC') + ': ' + (d.total | number: '1.0-2')"
              >
                <span class="seg seg--voice" [style.height]="barHeight(d.voice)"></span>
                <span class="seg seg--ai" [style.height]="barHeight(d.ai)"></span>
              </div>
            </div>
            <div class="axis" *ngIf="days.length">
              <span>{{ days[0].date | date: 'MMM d' : 'UTC' }}</span>
              <span>{{ days[days.length - 1].date | date: 'MMM d' : 'UTC' }}</span>
            </div>
          </div>
        </section>

        <section class="block" [class.is-stale]="isLoading">
          <h3 class="block-title">{{ 'Voice by space' | translate }}</h3>
          <ul class="rows" *ngIf="voice?.bySpace?.length; else noVoice">
            <li class="row" *ngFor="let s of voice?.bySpace">
              <div class="row-main">
                <span class="row-name" [title]="s.name">{{ s.name || ('Untitled space' | translate) }}</span>
                <span class="row-value">{{ s.credits | number: '1.0-2' }}</span>
              </div>
              <div class="row-sub">
                {{ s.calls }} {{ (s.calls === 1 ? 'call' : 'calls') | translate }}
              </div>
              <div class="share">
                <span class="share-fill share-fill--voice" [style.width]="share(s.credits, voice?.credits || 0)"></span>
              </div>
            </li>
          </ul>
          <ng-template #noVoice><p class="muted">{{ 'No voice calls this month.' | translate }}</p></ng-template>
        </section>
      </ng-container>
    </div>
  </div>
</div>
